import type { Domino, GameState } from '../types';
import { countDoubles } from '../core/dominoes';

export interface HandFeatureVector {
  suitLengths: number[];
  doubles: number;
  highControls: number;
  exposedCount: number;
  marksAhead: number;
  marksBehind: number;
  marksTied: boolean;
}

const HIGH_CONTROL_VALUES = new Set([5, 6]);

function suitLength(hand: Domino[], suit: number): number {
  return hand.filter((domino) => domino.high === suit || domino.low === suit).length;
}

function countHighControls(hand: Domino[]): number {
  return hand.filter((domino) => HIGH_CONTROL_VALUES.has(domino.high) || HIGH_CONTROL_VALUES.has(domino.low))
    .length;
}

function countExposedEnds(hand: Domino[]): number {
  const counts = new Map<number, number>();
  for (const domino of hand) {
    counts.set(domino.high, (counts.get(domino.high) ?? 0) + 1);
    counts.set(domino.low, (counts.get(domino.low) ?? 0) + 1);
  }

  let exposed = 0;
  for (const count of counts.values()) {
    if (count === 1) exposed += 1;
  }
  return exposed;
}

/**
 * Extract replay-safe features from a completed human hand.
 */
export function extractHandFeatures(
  hand: Domino[],
  marksBefore: [number, number],
  humanTeam = 0,
): HandFeatureVector {
  const humanMarks = marksBefore[humanTeam] ?? 0;
  const opponentMarks = marksBefore[1 - humanTeam] ?? 0;

  return {
    suitLengths: [0, 1, 2, 3, 4, 5, 6].map((suit) => suitLength(hand, suit)),
    doubles: countDoubles(hand),
    highControls: countHighControls(hand),
    exposedCount: countExposedEnds(hand),
    marksAhead: Math.max(0, humanMarks - opponentMarks),
    marksBehind: Math.max(0, opponentMarks - humanMarks),
    marksTied: humanMarks === opponentMarks,
  };
}

export function extractHandFeaturesFromState(
  state: GameState,
  humanPlayerIndex = 0,
): HandFeatureVector | null {
  const player = state.players[humanPlayerIndex];
  if (!player || player.hand.length === 0) {
    return null;
  }

  const teamId = player.teamId ?? humanPlayerIndex % 2;
  const marksBefore: [number, number] = [state.teamMarks[0] ?? 0, state.teamMarks[1] ?? 0];
  return extractHandFeatures(player.hand, marksBefore, teamId);
}
